import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import BookmarkRoundedIcon from '@mui/icons-material/BookmarkRounded';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { showAuth } from '../../redux/authSlice';
import { toast } from 'react-toastify';
import proxy from '../../api/axios';
import { getCachedBookmark, setCachedBookmark } from '../../common/bookmarkCache';
import { patchProblemBookmarkInCache } from '../../api/queries';

const ACCENT = '#137274';

const FEEDBACK_META = {
  report: {
    title: 'Report a problem',
    hint: 'Describe what’s wrong — typo, wrong sample, unclear statement, or a bug.',
    placeholder: 'What issue did you find?',
  },
  suggest_edit: {
    title: 'Suggest an edit',
    hint: 'Propose clearer wording, a better example, or a fix to the statement.',
    placeholder: 'Your suggested change…',
  },
  contribute_testcase: {
    title: 'Contribute a test case',
    hint: 'Share an input / output pair that would strengthen the judge tests.',
    placeholder: 'Why is this case useful? (edge case, regression, …)',
  },
};

/** Prev / next — slim text links (LeetCode-style), not heavy cards. */
export const ProblemNeighborNav = ({ neighbors, contestId, challenge }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const muted = isDark ? '#8b949e' : '#64748b';

  if (contestId || challenge) return null;
  if (!neighbors?.prev && !neighbors?.next) return null;

  const linkSx = (align) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.35,
    maxWidth: { xs: 148, sm: 220, md: 260 },
    minWidth: 0,
    textDecoration: 'none',
    color: muted,
    px: 0.75,
    py: 0.5,
    borderRadius: 1.25,
    justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
    transition: 'color 0.15s ease, background 0.15s ease',
    '&:hover': {
      color: ACCENT,
      bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.06),
      '& .nav-title': { color: ACCENT },
    },
  });

  const Side = ({ item, side }) => {
    if (!item?.slug) {
      return <Box sx={{ width: { xs: 148, sm: 220, md: 260 }, flexShrink: 0 }} />;
    }
    const isNext = side === 'next';
    return (
      <Tooltip title={item.title || item.slug} arrow placement="bottom">
        <Box
          component={RouterLink}
          to={`/problems/${item.slug}`}
          sx={linkSx(isNext ? 'right' : 'left')}
        >
          {!isNext && (
            <ChevronLeftRoundedIcon sx={{ fontSize: 20, flexShrink: 0, opacity: 0.85 }} />
          )}
          <Box sx={{ minWidth: 0, textAlign: isNext ? 'right' : 'left' }}>
            <Typography
              sx={{
                display: 'block',
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                lineHeight: 1.15,
                opacity: 0.85,
              }}
            >
              {isNext ? 'Next' : 'Prev'}
            </Typography>
            <Typography
              className="nav-title"
              sx={{
                display: 'block',
                fontSize: '0.8rem',
                fontWeight: 650,
                letterSpacing: '-0.02em',
                lineHeight: 1.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: isDark ? '#e6edf3' : '#0f2a2b',
                transition: 'color 0.15s',
              }}
            >
              {item.title}
            </Typography>
          </Box>
          {isNext && (
            <ChevronRightRoundedIcon sx={{ fontSize: 20, flexShrink: 0, opacity: 0.85 }} />
          )}
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        width: '100%',
        maxWidth: { md: 420 },
        ml: { md: 'auto' },
      }}
    >
      <Side item={neighbors.prev} side="prev" />
      <Box
        aria-hidden
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: alpha(muted, 0.45),
          flexShrink: 0,
          display: { xs: 'none', sm: 'block' },
        }}
      />
      <Side item={neighbors.next} side="next" />
    </Box>
  );
};

/** Icon-only prev/next for the focused solve panel header. */
export const ProblemNeighborNavInline = ({ neighbors }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const muted = isDark ? '#8b949e' : '#64748b';

  if (!neighbors?.prev && !neighbors?.next) return null;

  const btnSx = {
    color: muted,
    p: 0.65,
    '&:hover': { color: ACCENT, bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.06) },
    '&.Mui-disabled': { opacity: 0.35 },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        pr: 0.5,
        borderLeft: '1px solid',
        borderColor: isDark ? '#30363d' : 'rgba(15,60,70,0.1)',
        pl: 0.25,
      }}
    >
      <Tooltip title={neighbors.prev?.slug ? neighbors.prev.title : 'No previous problem'} arrow>
        <span>
          <IconButton
            component={neighbors.prev?.slug ? RouterLink : 'span'}
            to={neighbors.prev?.slug ? `/problems/${neighbors.prev.slug}` : undefined}
            size="small"
            disabled={!neighbors.prev?.slug}
            sx={btnSx}
            aria-label="Previous problem"
          >
            <ChevronLeftRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={neighbors.next?.slug ? neighbors.next.title : 'No next problem'} arrow>
        <span>
          <IconButton
            component={neighbors.next?.slug ? RouterLink : 'span'}
            to={neighbors.next?.slug ? `/problems/${neighbors.next.slug}` : undefined}
            size="small"
            disabled={!neighbors.next?.slug}
            sx={btnSx}
            aria-label="Next problem"
          >
            <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

/** Like + report / suggest / contribute — compact LeetCode-style action row. */
export const ProblemEngagementBar = ({
  slug,
  initialLiked = false,
  initialLikeCount = 0,
  initialBookmarked = false,
  contestId,
  challenge,
  onLikeChange,
  onBookmarkChange,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);

  const [liked, setLiked] = useState(Boolean(initialLiked));
  const [likeCount, setLikeCount] = useState(initialLikeCount || 0);
  const [liking, setLiking] = useState(false);
  const [bookmarked, setBookmarked] = useState(() => {
    const cached = getCachedBookmark(slug);
    return cached !== null ? cached : Boolean(initialBookmarked);
  });
  const [bookmarking, setBookmarking] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [message, setMessage] = useState('');
  const [testInput, setTestInput] = useState('');
  const [testOutput, setTestOutput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const muted = isDark ? '#8b949e' : '#64748b';
  const surface = isDark ? alpha('#fff', 0.03) : alpha(ACCENT, 0.03);

  useEffect(() => {
    setLiked(Boolean(initialLiked));
    setLikeCount(initialLikeCount || 0);
  }, [slug, initialLiked, initialLikeCount]);

  useEffect(() => {
    // A locally cached toggle (from this device, possibly still in flight or
    // made offline) is more likely to reflect the user's actual intent than
    // a prop that may be a moment stale — prefer it when present.
    const cached = getCachedBookmark(slug);
    setBookmarked(cached !== null ? cached : Boolean(initialBookmarked));
  }, [slug, initialBookmarked]);

  const meta = useMemo(
    () => (dialogType ? FEEDBACK_META[dialogType] : null),
    [dialogType],
  );

  if (contestId || challenge) return null;

  const requireAuth = () => {
    if (!isLoggedIn) {
      dispatch(showAuth());
      return false;
    }
    return true;
  };

  const handleToggleLike = async () => {
    if (!requireAuth()) return;
    if (!slug || liking) return;
    setLiking(true);
    try {
      const { data } = await proxy.post(`/problems/${slug}/like`);
      setLiked(data.liked);
      setLikeCount(data.likeCount);
      onLikeChange?.(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update like');
    } finally {
      setLiking(false);
    }
  };

  const handleToggleBookmark = async () => {
    if (!requireAuth()) return;
    if (!slug || bookmarking) return;

    // Optimistic: flip immediately so the click feels instant instead of
    // waiting on the round trip — previously the icon only updated once the
    // request resolved, so a slow/flaky connection made clicks look like
    // they'd done nothing, which is what led to clicking repeatedly.
    const previous = bookmarked;
    const next = !previous;
    setBookmarked(next);
    setCachedBookmark(slug, next);
    patchProblemBookmarkInCache(slug, next);
    setBookmarking(true);
    try {
      const { data } = await proxy.post(`/problems/${slug}/bookmark`);
      setBookmarked(data.isBookmarked);
      setCachedBookmark(slug, data.isBookmarked);
      patchProblemBookmarkInCache(slug, data.isBookmarked);
      onBookmarkChange?.(data);
    } catch (err) {
      // Request didn't actually take effect server-side — roll back.
      setBookmarked(previous);
      setCachedBookmark(slug, previous);
      patchProblemBookmarkInCache(slug, previous);
      toast.error(err.response?.data?.message || 'Could not update bookmark');
    } finally {
      setBookmarking(false);
    }
  };

  const openDialog = (type) => {
    if (!requireAuth()) return;
    setDialogType(type);
    setMessage('');
    setTestInput('');
    setTestOutput('');
  };

  const closeDialog = () => {
    if (submitting) return;
    setDialogType(null);
  };

  const handleSubmitFeedback = async () => {
    if (!dialogType) return;
    setSubmitting(true);
    try {
      await proxy.post(`/problems/${slug}/feedback`, {
        type: dialogType,
        message,
        testCaseInput: testInput,
        testCaseOutput: testOutput,
      });
      toast.success('Thanks — we received your submission.');
      setDialogType(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  const iconBtnSx = {
    width: 34,
    height: 34,
    borderRadius: 1.5,
    color: muted,
    border: `1px solid transparent`,
    '&:hover': {
      color: ACCENT,
      bgcolor: alpha(ACCENT, isDark ? 0.14 : 0.08),
      borderColor: alpha(ACCENT, 0.2),
    },
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          mb: 2,
          px: 1,
          py: 0.65,
          borderRadius: 2,
          border: `1px solid ${border}`,
          bgcolor: surface,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Tooltip title={liked ? 'Unlike' : 'Like'} arrow>
            <span>
              <IconButton
                size="small"
                disabled={liking}
                onClick={handleToggleLike}
                aria-label={liked ? 'Unlike problem' : 'Like problem'}
                sx={{
                  ...iconBtnSx,
                  color: liked ? ACCENT : muted,
                  bgcolor: liked ? alpha(ACCENT, isDark ? 0.2 : 0.1) : 'transparent',
                }}
              >
                {liked ? (
                  <ThumbUpAltIcon sx={{ fontSize: 18 }} />
                ) : (
                  <ThumbUpAltOutlinedIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            component="span"
            sx={{
              fontSize: '0.8rem',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              color: liked ? ACCENT : muted,
              minWidth: 16,
              mr: 0.5,
            }}
          >
            {likeCount > 0 ? likeCount : ''}
          </Typography>

          <Tooltip title={bookmarked ? 'Remove bookmark' : 'Bookmark'} arrow>
            <span>
              <IconButton
                size="small"
                disabled={bookmarking}
                onClick={handleToggleBookmark}
                aria-label={bookmarked ? 'Remove bookmark' : 'Bookmark problem'}
                sx={{
                  ...iconBtnSx,
                  color: bookmarked ? ACCENT : muted,
                  bgcolor: bookmarked ? alpha(ACCENT, isDark ? 0.2 : 0.1) : 'transparent',
                }}
              >
                {bookmarked ? (
                  <BookmarkRoundedIcon sx={{ fontSize: 18 }} />
                ) : (
                  <BookmarkBorderRoundedIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Divider
            orientation="vertical"
            flexItem
            sx={{ mx: 0.5, borderColor: border, my: 0.75 }}
          />

          <Tooltip title="Suggest an edit" arrow>
            <IconButton
              size="small"
              onClick={() => openDialog('suggest_edit')}
              aria-label="Suggest an edit"
              sx={iconBtnSx}
            >
              <EditNoteOutlinedIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Contribute a test case" arrow>
            <IconButton
              size="small"
              onClick={() => openDialog('contribute_testcase')}
              aria-label="Contribute a test case"
              sx={iconBtnSx}
            >
              <ScienceOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Report a problem" arrow>
            <IconButton
              size="small"
              onClick={() => openDialog('report')}
              aria-label="Report a problem"
              sx={iconBtnSx}
            >
              <FlagOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            color: muted,
            letterSpacing: '0.01em',
            display: { xs: 'none', sm: 'block' },
            pr: 0.5,
          }}
        >
          Help improve this problem
        </Typography>
      </Box>

      <Dialog
        open={Boolean(dialogType)}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${border}`,
            bgcolor: isDark ? '#161b22' : '#fff',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          sx={{
            position: 'relative',
            fontWeight: 800,
            letterSpacing: '-0.025em',
            fontSize: '1.15rem',
            pr: 6,
            pb: 0.75,
          }}
        >
          {meta?.title}
          <IconButton
            onClick={closeDialog}
            disabled={submitting}
            sx={{ position: 'absolute', right: 12, top: 12 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: muted, mb: 2, lineHeight: 1.5, fontSize: '0.875rem' }}
          >
            {meta?.hint}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Details"
            placeholder={meta?.placeholder}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={submitting}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
          />
          {dialogType === 'contribute_testcase' && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Input"
                placeholder="Judge input…"
                value={testInput}
                onChange={(e) => setTestInput(e.target.value)}
                disabled={submitting}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                InputProps={{
                  sx: {
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: '0.85rem',
                  },
                }}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Expected output"
                placeholder="Expected output…"
                value={testOutput}
                onChange={(e) => setTestOutput(e.target.value)}
                disabled={submitting}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                InputProps={{
                  sx: {
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: '0.85rem',
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={closeDialog}
            disabled={submitting}
            sx={{ textTransform: 'none', fontWeight: 650, color: muted }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitFeedback}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontWeight: 750,
              borderRadius: 2,
              px: 2.25,
              bgcolor: ACCENT,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
            }}
          >
            {submitting ? 'Sending…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProblemEngagementBar;
